import { resolveNoddiDualTldUrl } from './resolveNoddiDualTldUrl';

// First-party browser ingest hosts for third-party SaaS (Sentry, Mixpanel).
// Named for the signal, not the vendor, and distinct from Faro (`telemetry.*`)
// and OpenPanel (`analytics.*`). Dual-TLD rewrite is handled by
// `resolveNoddiDualTldUrl`. Traefik reverse-proxies these hosts (no sidecar):
// noddi-infrastructure `services/traefik/first_party_ingest.py`.

export const SENTRY_FIRST_PARTY_ORIGIN = 'https://errors.noddi.co';

export const MIXPANEL_FIRST_PARTY_API_HOST = 'https://signals.noddi.co';

// Traefik reverse-proxies `/api/<projectId>/envelope/` to this org's ingest
// host. The Sentry JS `tunnel` option posts the envelope to that exact URL —
// no ingest path and no auth params are appended (`getEnvelopeEndpointWithUrlEncodedAuth`
// returns `tunnel` verbatim), so both the project id and the `sentry_key` must
// ride on the URL. Traefik passes the query through untouched and ingest
// authenticates from it; the key is public (it is in the DSN bundle anyway).
function parseHttpUrl(value: string): URL | undefined {
  try {
    return new URL(value);
  } catch {
    return undefined;
  }
}

export function sentryFirstPartyTunnel(dsn: string | undefined, hostname?: string): string | undefined {
  if (!dsn) {
    return undefined;
  }
  const parsed = parseHttpUrl(dsn);
  if (!parsed) {
    return undefined;
  }
  const projectId = parsed.pathname.replace(/^\//, '');
  if (!/^\d+$/.test(projectId) || !parsed.username) {
    return undefined;
  }
  const origin = resolveNoddiDualTldUrl(SENTRY_FIRST_PARTY_ORIGIN, hostname) ?? SENTRY_FIRST_PARTY_ORIGIN;
  return `${origin.replace(/\/$/, '')}/api/${projectId}/envelope/?sentry_key=${encodeURIComponent(
    parsed.username
  )}&sentry_version=7`;
}

// Ad-block lists match `*/track` on any host. Mixpanel's `api_routes` lets us
// POST to innocuous first-party paths; the ingest proxy maps them back to
// Mixpanel's real `/track/` `/engage/` `/groups/` `/record/` `/flags/`.
export const MIXPANEL_FIRST_PARTY_API_ROUTES = {
  track: 'e/',
  engage: 'p/',
  groups: 'g/',
  record: 'r/',
  flags: 'f/'
} as const;
